import React from "react";
import { CreditCard } from "lucide-react";
import { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * "My Subscription" right-rail card: sponsor notice, free-plan state and the
 * paid-membership state with portal / cancel / resume actions.
 *
 * Extracted verbatim from src/pages/Dashboard.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged.
 */
export default function SubscriptionCard({
  user,
  billing,
  navigate,
  onOpenPortal,
  onResume,
  onRequestCancel,
}) {
  return (
    <section data-testid="subscription-card" className="sm-card p-6 bg-white">
      <div className="flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-[#1F6F78]" />
        <h2 className="font-display text-xl font-bold text-[#1F2A2E]">My Plan</h2>
      </div>
      {user.account_type === "sponsor" ? (
        <p className="mt-3 text-sm text-[#1F7A4D] bg-[#E4F3EA] rounded-xl px-3 py-2 font-semibold">
          Sponsor account: unlimited messaging, free forever.
        </p>
      ) : billing?.subscription_status === "free" || !billing?.subscription_plan ? (
        <>
          <p className="mt-3 text-sm text-[#4B5A60]">
            You are on the free plan with{" "}
            <strong className="text-[#1F2A2E]">
              {billing?.free_messages_remaining ?? 0} of {user.free_message_allowance}
            </strong>{" "}
            free messages remaining.
          </p>
          {user.free_pass_expires_at && new Date(user.free_pass_expires_at) > new Date() && (
            <p className="mt-2 text-sm font-semibold text-[#8A5A12] bg-[#FBF0DC] rounded-lg px-3 py-2">
              Recovery Access Pass active until {formatDate(user.free_pass_expires_at)}
            </p>
          )}
          <Button
            onClick={() => navigate("/pricing")}
            data-testid="dashboard-view-plans"
            className="mt-4 w-full bg-[#1F6F78] hover:bg-[#185B62] text-white"
          >
            View Plans
          </Button>
        </>
      ) : (
        <>
          <p className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">
            {billing.plan_label} Membership
          </p>
          <p className="text-sm text-[#4B5A60]">
            {billing.plan_price}
            {billing.plan_period}
          </p>
          <p className="mt-2 text-sm text-[#4B5A60]">
            Status:{" "}
            <span
              data-testid="subscription-status"
              className={`font-semibold ${
                billing.subscription_status === "active"
                  ? "text-[#1F7A4D]"
                  : billing.subscription_status === "past_due"
                  ? "text-[#B7791F]"
                  : "text-[#B42318]"
              }`}
            >
              {billing.subscription_status}
            </span>
          </p>
          {billing.subscription_end_date && (
            <p className="mt-1 text-sm text-[#4B5A60]">
              {billing.cancel_at_period_end
                ? `Access until ${formatDate(billing.subscription_end_date)} - it will not renew`
                : `Your plan renews on ${formatDate(billing.subscription_end_date)} (${
                    billing.plan_price
                  }${billing.plan_period}, automatically, until you cancel)`}
            </p>
          )}
          <div className="mt-4 space-y-2">
            <Button
              onClick={onOpenPortal}
              data-testid="manage-subscription-button"
              variant="outline"
              className="w-full bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0]"
            >
              Manage Plan &amp; Billing
            </Button>
            {billing.cancel_at_period_end ? (
              <Button
                onClick={onResume}
                data-testid="resume-subscription-button"
                className="w-full bg-[#1F6F78] hover:bg-[#185B62] text-white"
              >
                Resume auto-renewal
              </Button>
            ) : (
              <Button
                onClick={onRequestCancel}
                data-testid="cancel-subscription-button"
                variant="ghost"
                className="w-full text-[#B42318] hover:bg-[#FBE6E4]"
              >
                Cancel Plan
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
